import { Injectable } from '@angular/core';
import {
  DoughnutChartConfig,
  DoughnutChartData,
  LineChartConfig,
  LineChartData,
  StackBarChartConfig,
  StackBarData,
} from '../interfaces/chart-data.interface';
import {
  DEFAULT_DOUGHNUT_CONFIG,
  DEFAULT_LINE_CHART_CONFIG,
  DEFAULT_STACK_BAR_CONFIG,
  LINE_CHART_AXIS_STYLES,
  LINE_CHART_COLORS,
  STACK_BAR_AXIS_STYLES,
} from '../constants/chart-defaults';

@Injectable({
  providedIn: 'root',
})
export class ChartConfigService {
  getDoughnutChartOptions(
    data: DoughnutChartData[],
    config: Partial<DoughnutChartConfig> = {},
    title = 'Chart',
    totalCount?: number
  ) {
    const finalConfig = {
      ...DEFAULT_DOUGHNUT_CONFIG.DOUGHNUT,
      ...config.DOUGHNUT,
    };

    return {
      tooltip: {
        trigger: 'item',
      },
      legend: {
        ...DEFAULT_DOUGHNUT_CONFIG.DOUGHNUT_LEGEND_STYLES,
        ...config.DOUGHNUT_LEGEND_STYLES,
      },
      color:
        config.DOUGHNUT?.colors ?? DEFAULT_DOUGHNUT_CONFIG.DOUGHNUT?.colors,
      series: [
        {
          name: title,
          type: 'pie',
          radius: [finalConfig.innerRadius, finalConfig.outerRadius],
          ...(finalConfig.center?.length && { center: finalConfig.center }),
          avoidLabelOverlap: false,
          label: {
            show: false,
            position: 'center',
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 14,
              fontWeight: 'bold',
            },
          },
          labelLine: {
            show: false,
          },
          data,
          ...(totalCount !== undefined && {
            title: {
              show: true,
              text: totalCount.toString(),
              textStyle: {
                fontSize: 24,
                fontWeight: 'bold',
                color: '#475467',
              },
              subtext: 'Total',
              subtextStyle: {
                fontSize: 14,
                color: '#667085',
              },
              left: 'center',
              top: 'center',
            },
          }),
        },
      ],
    };
  }
  getStackBarChartOptions(
    series: StackBarData[],
    config: Partial<StackBarChartConfig> = {}
  ) {
    const finalConfig = { ...DEFAULT_STACK_BAR_CONFIG, ...config };
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'none',
        },
      },
      legend: {
        top: '5%',
        right: '5%',
        orient: 'horizontal',
        icon: 'circle',
        itemWidth: 8,
        itemHeight: 8,
        textStyle: {
          fontSize: 14,
          fontWeight: '400',
          color: '#475467',
        },
      },
      grid: {
        ...finalConfig.grid,
      },
      xAxis: {
        type: 'category',
        data: finalConfig.xAxisData,
        ...STACK_BAR_AXIS_STYLES,
        name: finalConfig.xAxisName,
        nameLocation: 'middle',
        nameGap: 25,
      },
      yAxis: {
        type: 'value',
        ...STACK_BAR_AXIS_STYLES,
        name: finalConfig.yAxisName,
        nameLocation: 'middle',
        nameGap: 45,
      },
      series: series.map(item => ({
        name: item.name,
        type: 'bar',
        stack: finalConfig.stack,
        data: item.data,
        itemStyle: {
          color: item.color,
          borderRadius: item.borderRadius,
        },
        emphasis: item.emphasis,
      })),
    };
  }

  getLineChartOptions(
    series: LineChartData[],
    config: Partial<LineChartConfig> = {}
  ) {
    const finalConfig = { ...DEFAULT_LINE_CHART_CONFIG, ...config };

    return {
      tooltip: {
        trigger: 'axis',
      },
      legend: {
        top: '5%',
        right: '5%',
        orient: 'horizontal',
        icon: 'circle',
        itemWidth: 8,
        itemHeight: 8,
        textStyle: {
          fontWeight: '400',
          color: '#475467',
        },
      },
      grid: {
        ...finalConfig.grid,
      },
      xAxis: {
        type: 'category',
        data: finalConfig.xAxisData,
        ...LINE_CHART_AXIS_STYLES,
        name: finalConfig.xAxisName,
        nameLocation: 'middle',
        nameGap: 30,
        axisLabel: {
          color: LINE_CHART_COLORS.AXIS_TEXT,
        },
      },
      yAxis: {
        type: 'value',
        min: finalConfig.yAxisMin,
        max: finalConfig.yAxisMax,
        interval: finalConfig.yAxisInterval,
        ...LINE_CHART_AXIS_STYLES,
        name: finalConfig.yAxisName,
        nameLocation: 'middle',
        nameGap: 30,
      },
      series: series.map(item => ({
        name: item.name,
        type: 'line',
        data: item.data,
        showSymbol: item.showSymbol ?? false,
        symbolSize: item.symbolSize,
        smooth: item.smooth ?? false,
        lineStyle: {
          color: item.color,
        },
        ...(item.areaStyle && {
          areaStyle: {
            opacity: item.areaStyle.opacity ?? 0.1,
            color: item.areaStyle.color ?? item.color,
          },
        }),
      })),
    };
  }
}
